/*
 * DBComplianceMatrix — per-technology compliance-scope editor.
 *
 * WHAT: A grid for ONE technology where rows are database instances/mappings that
 *       expand to discovered sub-units (MSSQL databases / Oracle PDBs / MySQL schemas /
 *       Cassandra keyspaces / PostgreSQL databases) and columns are the compliance
 *       frameworks the databases' applications mandate (dynamic — only mandated
 *       frameworks appear). Each cell is an in-scope checkbox. Sub-units come from
 *       db_instance_metadata, populated when the connection is tested/refreshed.
 *
 * WHY:  Compliance is application-derived: the owning app's compliance_flags decide
 *       WHICH frameworks apply to a database. This screen decides, WITHIN an applicable
 *       framework, whether a given instance / sub-unit is in scope for that
 *       framework's campaigns + access reports. A database out of scope for SOX may be
 *       in scope for HIPAA — ticking/unticking writes an explicit override on top of the
 *       app-derived default. Non-applicable cells (app doesn't mandate) are not editable.
 *
 * RBAC: per-tech, gated by can_manage_<tech>_privilege_mappings — placed under each
 *       technology's Administration menu beside Privilege Mappings.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import DatabaseMappingModal from './common/DatabaseMappingModal';
import DatabaseDeleteAction from './DatabaseDeleteAction';
import OracleConnections from './OracleConnections';
import MssqlConnections from './MssqlConnections';
import MySqlConnections from './MySqlConnections';
import PostgresqlConnections from './PostgresqlConnections';
import CassandraConnections from './CassandraConnections';

// Map a technology (lower-case) to its connection-management component. After a
// database is created in the matrix, we render the matching one in "embedded"
// mode so the DBA can create the connection without leaving this screen.
const CONN_COMPONENTS = {
  oracle: OracleConnections,
  mssql: MssqlConnections,
  mysql: MySqlConnections,
  postgresql: PostgresqlConnections,
  cassandra: CassandraConnections,
};

// Blank create/edit form for a database mapping (mirrors AppDatabaseMapping.js).
const EMPTY_FORM = {
  app_id: '',
  database_name: '',
  database_identifier: '',
  display_name: '',
  database_type: 'Oracle',
  database_version: '',
  database_environment: '',
  deployment_type: 'STANDALONE',
  node_topology: null,
  is_primary_db: '',
  notes: '',
  mssql_instance_name: '',
  mssql_target_databases: [],
  mssql_include_system_dbs: false,
};

const DBComplianceMatrix = ({ technology, onNavigateToApplication }) => {
  const { user } = useAuth();
  const currentUser = user?.username || user?.email || 'unknown_user';
  const techLower = (technology || '').toLowerCase();

  const [card, setCard] = useState(null);
  const [unitScopeEnforced, setUnitScopeEnforced] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});      // { database_identifier: bool }
  const [notice, setNotice] = useState(null);
  const [showInactive, setShowInactive] = useState(false);  // include soft-deleted DBs

  // --- Registry CRUD state (matrix is the management surface) ---
  const [applications, setApplications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  // Save errors while the mapping modal is OPEN must render INSIDE the modal —
  // the page-level notice sits behind the overlay where the user can't see it.
  const [crudError, setCrudError] = useState(null);
  const [modalMode, setModalMode] = useState('create');   // 'create' | 'edit'
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingKey, setEditingKey] = useState(null);     // {app_id, database_name} of row being edited
  const [crudSaving, setCrudSaving] = useState(false);
  // After creating a database, hold {database_identifier, display_name} to drive
  // the auto-opened connection-creation popup; null = no popup.
  const [connPopup, setConnPopup] = useState(null);
  const fileInputRef = React.useRef(null);
  const [importing, setImporting] = useState(false);
  // M:N link-application picker: the db row being linked (null = closed) + selection.
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkAppId, setLinkAppId] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const authHeaders = useMemo(
    () => ({ headers: { 'X-Username': currentUser } }),
    [currentUser]
  );

  const fetchCard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(
        API_CONFIG.getDatabasesEndpoint(
          `db-compliance-matrix/${techLower}/config/${showInactive ? '?include_inactive=true' : ''}`),
        authHeaders
      );
      const dbs = resp.data.card?.databases || [];
      console.log(`[Matrix ${techLower}] fetched showInactive=${showInactive} →`,
        dbs.length, 'rows; inactive:', dbs.filter(d => d.is_active === false).map(d => d.database_identifier));
      setCard(resp.data.card);
      setUnitScopeEnforced(resp.data.unit_scope_enforced !== false);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load compliance matrix');
    } finally {
      setLoading(false);
    }
  }, [techLower, authHeaders, showInactive]);

  // Applications drive the owner dropdown in the create modal.
  const fetchApplications = useCallback(async () => {
    try {
      const resp = await axios.get(API_CONFIG.getDatabasesEndpoint('applications/'), authHeaders);
      setApplications(Array.isArray(resp.data) ? resp.data : (resp.data?.applications || []));
    } catch {
      setApplications([]);
    }
  }, [authHeaders]);

  useEffect(() => { fetchCard(); fetchApplications(); }, [fetchCard, fetchApplications]);

  const toggleExpand = (ident) =>
    setExpanded((prev) => ({ ...prev, [ident]: !prev[ident] }));

  /* Apply one edit (set in_scope, or reset to default) and refresh. */
  const applyEdit = async (edit) => {
    setSaving(true);
    setNotice(null);
    try {
      const resp = await axios.put(
        API_CONFIG.getDatabasesEndpoint(`db-compliance-matrix/${techLower}/config/update/`),
        { edits: [edit] },
        authHeaders
      );
      const data = resp.data;
      if (data.rejected && data.rejected.length) {
        setNotice({ type: 'warning', text: data.rejected[0].reason || 'Edit rejected' });
      } else if (data.affected_pre_attestation_campaigns) {
        setNotice({
          type: 'info',
          text: `Saved. ${data.affected_pre_attestation_campaigns} pre-attestation campaign(s) will pick up this scope on next open.`,
        });
      }
      await fetchCard();
    } catch (e) {
      setNotice({ type: 'danger', text: e.response?.data?.error || e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const onToggleCell = (ident, framework, physical_db, cell) => {
    if (!cell || !cell.applicable) return;     // non-applicable cells are not editable
    applyEdit({
      framework,
      database_identifier: ident,
      physical_db: physical_db || undefined,
      in_scope: !cell.in_scope,
    });
  };

  const onResetCell = (ident, framework, physical_db) => {
    applyEdit({ framework, database_identifier: ident, physical_db: physical_db || undefined, reset: true });
  };

  // ---- Registry CRUD handlers (delegate to the per-tech matrix endpoints) ----
  const openCreate = () => {
    setModalMode('create');
    setEditingKey(null);
    setCrudError(null);
    setFormData({ ...EMPTY_FORM, database_type: card?.label || technology, deployment_type: 'STANDALONE' });
    setShowModal(true);
  };

  const openEdit = (db) => {
    setModalMode('edit');
    setEditingKey({ app_id: db.app_id, database_name: db.database_name || db.display_name });
    setFormData({
      ...EMPTY_FORM,
      app_id: db.app_id || '',
      database_name: db.database_name || db.display_name || '',
      database_identifier: db.database_identifier || '',
      display_name: db.display_name || '',
      database_type: card?.label || technology,
      database_version: db.database_version || '',
      database_environment: db.database_environment || '',
      deployment_type: db.deployment_type || 'STANDALONE',
      node_topology: db.node_topology || null,
      is_primary_db: db.is_primary_db || '',
      notes: db.notes || '',
      mssql_instance_name: db.instance || db.mssql_instance_name || '',
      mssql_target_databases: db.mssql_target_databases || [],
      mssql_include_system_dbs: db.mssql_include_system_dbs || false,
    });
    setCrudError(null);
    setShowModal(true);
  };

  const handleFormChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  // Persist a create or edit through the matrix's tech-gated endpoints.
  const handleSaveMapping = async () => {
    setCrudSaving(true);
    setNotice(null);
    setCrudError(null);
    try {
      const base = `db-compliance-matrix/${techLower}/mappings`;
      if (modalMode === 'create') {
        await axios.post(
          API_CONFIG.getDatabasesEndpoint(`${base}/create/`),
          { ...formData, database_type: card?.label || technology },
          authHeaders
        );
        // Capture the new database's identity BEFORE we reset the form so the
        // follow-up connection popup can prefill + lock it.
        const newIdent = formData.database_identifier;
        const newDisplay = formData.display_name || formData.database_name || newIdent;
        setShowModal(false);
        await fetchCard();
        // Open the tech-specific connection-creation form (full inline form) so the
        // user can immediately create the connection that activates this database.
        // Only fires on CREATE — editing an existing DB does not pop the form.
        setConnPopup({ database_identifier: newIdent, display_name: newDisplay });
        setNotice({
          type: 'info',
          text: 'Database added. Create its connection in the popup to activate it.',
        });
      } else {
        const { app_id, database_name } = editingKey;
        await axios.put(
          API_CONFIG.getDatabasesEndpoint(
            `${base}/${encodeURIComponent(app_id)}/${encodeURIComponent(database_name)}/update/`),
          formData,
          authHeaders
        );
        setShowModal(false);
        setNotice({ type: 'success', text: 'Database updated.' });
        await fetchCard();
      }
    } catch (e) {
      // The modal is still open on failure — put the error inside it, where the
      // user is looking, not in the page banner hidden behind the overlay.
      setCrudError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setCrudSaving(false);
    }
  };

  const handleDeactivate = async (db) => {
    // M:N: deactivating the DATABASE archives EVERY application link at once
    // (the whole-database route), so spell out the blast radius when shared.
    const activeApps = (db.apps || []).filter((a) => a.is_active !== false);
    const appList = activeApps
      .map((a) => `${a.app_name || a.app_id}${a.is_primary ? ' (primary)' : ''}`)
      .join(', ');
    if (!window.confirm(
      `Deactivate ${db.display_name || db.database_identifier}?\n\n` +
      (activeApps.length > 1
        ? `This database serves ${activeApps.length} applications — deactivating removes it ` +
          `from compliance scope and new campaigns for ALL of them: ${appList}.\n\n`
        : '') +
      'It is marked inactive (not deleted) — it leaves compliance scope and new campaigns, ' +
      'but completed campaigns and historical data are preserved. You can reactivate it later.\n\n' +
      'Deactivation is blocked if the database is in an active campaign.'
    )) return;
    setNotice(null);
    try {
      const resp = await axios.delete(
        API_CONFIG.getDatabasesEndpoint(
          `db-compliance-matrix/${techLower}/databases/${encodeURIComponent(db.database_identifier)}/deactivate/`),
        authHeaders
      );
      setNotice({ type: 'success', text: resp.data?.message || 'Database deactivated.' });
      await fetchCard();
    } catch (e) {
      const d = e.response?.data;
      setNotice({ type: 'danger', text: d?.error || (d?.active_campaigns ? `In use by active campaign(s): ${d.active_campaigns}` : e.message) });
    }
  };

  // Reactivate a soft-deleted mapping (flip is_active back on). Uses the deactivate
  // route's POST {reactivate:true} branch. Only reachable when "Show inactive" is on.
  const handleReactivate = async (db) => {
    if (!window.confirm(
      `Reactivate ${db.display_name || db.database_identifier}?\n\n` +
      'It returns to the compliance matrix and becomes eligible for new campaigns. ' +
      'Create a connection for it (if none exists) before it can be collected.'
    )) return;
    setNotice(null);
    try {
      await axios.post(
        API_CONFIG.getDatabasesEndpoint(
          `db-compliance-matrix/${techLower}/mappings/${encodeURIComponent(db.app_id)}/${encodeURIComponent(db.database_name || db.display_name)}/deactivate/`),
        { reactivate: true },
        authHeaders
      );
      setNotice({ type: 'success', text: 'Database reactivated.' });
      await fetchCard();
    } catch (e) {
      setNotice({ type: 'danger', text: e.response?.data?.error || e.message || 'Reactivate failed' });
    }
  };

  // ---- M:N application-link actions (backend re-enforces the per-tech DBA gate) ----
  // Link an additional application to an existing database (secondary link).
  const handleLinkApp = async () => {
    if (!linkTarget || !linkAppId) return;
    setLinkSaving(true);
    setNotice(null);
    try {
      await axios.post(
        API_CONFIG.getDatabasesEndpoint(`db-compliance-matrix/${techLower}/mappings/link/`),
        { app_id: linkAppId, database_identifier: linkTarget.database_identifier },
        authHeaders
      );
      setNotice({ type: 'success', text: `${linkAppId} linked to ${linkTarget.display_name || linkTarget.database_identifier}.` });
      setLinkTarget(null);
      setLinkAppId('');
      await fetchCard();
    } catch (e) {
      setNotice({ type: 'danger', text: e.response?.data?.error || e.message || 'Link failed' });
    } finally {
      setLinkSaving(false);
    }
  };

  // Transfer the primary flag to another linked application.
  const handleSetPrimary = async (db, app) => {
    if (!window.confirm(
      `Make ${app.app_name || app.app_id} the PRIMARY application for ` +
      `${db.display_name || db.database_identifier}?\n\n` +
      "The primary application's owner becomes the attester for this database in future " +
      'campaign reports, and the report shows it as the owning application. ' +
      'Existing campaigns and manual attester reassignments are unaffected.'
    )) return;
    setNotice(null);
    try {
      await axios.post(
        API_CONFIG.getDatabasesEndpoint(`db-compliance-matrix/${techLower}/mappings/set-primary/`),
        { app_id: app.app_id, database_identifier: db.database_identifier },
        authHeaders
      );
      setNotice({ type: 'success', text: `${app.app_name || app.app_id} is now the primary application.` });
      await fetchCard();
    } catch (e) {
      setNotice({ type: 'danger', text: e.response?.data?.error || e.message || 'Set primary failed' });
    }
  };

  // Detach a SECONDARY application link. The database itself stays registered under
  // its primary app; the backend rejects detaching the primary while others remain.
  const handleDetachApp = async (db, app) => {
    if (!window.confirm(
      `Detach ${app.app_name || app.app_id} from ${db.display_name || db.database_identifier}?\n\n` +
      "Only this application's link is removed — the database stays registered under its " +
      'primary application and keeps its compliance scope and campaign history.'
    )) return;
    setNotice(null);
    try {
      await axios.delete(
        API_CONFIG.getDatabasesEndpoint(
          `db-compliance-matrix/${techLower}/mappings/${encodeURIComponent(app.app_id)}/${encodeURIComponent(app.database_name || db.database_name || db.display_name)}/deactivate/`),
        authHeaders
      );
      setNotice({ type: 'success', text: `${app.app_name || app.app_id} detached.` });
      await fetchCard();
    } catch (e) {
      const d = e.response?.data;
      setNotice({ type: 'danger', text: d?.error || d?.message || e.message || 'Detach failed' });
    }
  };

  // CSV import/export reuse the shared mapping endpoints but SCOPED to this page's
  // technology (?database_type= / form field) so the buttons' scope matches the
  // page's scope. Import silently auto-filters foreign-tech rows — the estate-wide
  // export file round-trips cleanly here, applying only this tech's rows.
  const techLabel = card?.label || technology;
  const handleExportCSV = () => {
    if (window.confirm(`Export ${techLabel} database mappings to CSV?`)) {
      window.location.href = API_CONFIG.getDatabasesEndpoint(
        `app-database-mappings/export-csv/?database_type=${encodeURIComponent(techLabel)}`);
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { alert('Please select a CSV file'); return; }
    if (!window.confirm(
      `Import ${techLabel} mappings from CSV (rows for other technologies are ignored). ` +
      'Upsert-only: adds new mappings and updates existing ones by (app, identifier). ' +
      'It never deletes or deactivates — rows it cannot safely apply are skipped and reported. Continue?'
    )) { if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    setImporting(true);
    setNotice(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('database_type', techLabel);
    try {
      const resp = await axios.post(
        API_CONFIG.getDatabasesEndpoint('app-database-mappings/import-csv/'),
        fd, { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } }
      );
      // Skipped rows are genuine within-tech problems (e.g. identifier owned by another
      // app) and must be surfaced; foreign-tech rows are filtered server-side silently
      // and never appear in this list.
      const skipped = resp.data.skipped || [];
      const summary = `Import complete. Added ${resp.data.added ?? 0}, updated ${resp.data.updated ?? 0}` +
        (skipped.length ? `, skipped ${skipped.length}.` : '.');
      const details = skipped.map(s => `Line ${s.line}: ${s.reason}`).join(' — ');
      setNotice({
        type: skipped.length ? 'warning' : 'success',
        text: skipped.length ? `${summary} ${details}` : summary
      });
      await fetchCard();
    } catch (e) {
      setNotice({ type: 'danger', text: e.response?.data?.error || e.message || 'Import failed' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const frameworks = card?.frameworks || [];
  const databases = card?.databases || [];

  /* Render the single Compliance cell: one labeled in-scope checkbox per APPLICABLE
   * framework (only frameworks the database's app mandates appear). Ticking toggles the
   * db_compliance_scope in-scope override; an override row shows a reset-to-default link.
   * `cells` is the per-framework map for this row (instance or sub-unit). */
  const renderComplianceCell = (ident, physical_db, cells) => {
    // Only frameworks the app mandates are applicable; show those as toggles.
    const applicable = frameworks.filter((fw) => cells?.[fw]?.applicable);
    if (applicable.length === 0) {
      return <span className="text-muted" title="No compliance frameworks mandated by this database's application">—</span>;
    }
    return (
      <div className="d-flex flex-wrap gap-3">
        {applicable.map((fw) => {
          const cell = cells[fw];
          const id = `cmp-${ident}-${physical_db || 'inst'}-${fw}`;
          return (
            <span key={fw} className="d-inline-flex align-items-center">
              <input
                type="checkbox"
                className="form-check-input me-1"
                style={{ cursor: 'pointer' }}
                id={id}
                checked={!!cell.in_scope}
                disabled={saving}
                onChange={() => onToggleCell(ident, fw, physical_db, cell)}
                title={cell.is_override
                  ? `Override by ${cell.last_modified_by || 'unknown'}${cell.last_modified_at ? ' on ' + new Date(cell.last_modified_at).toLocaleString() : ''}`
                  : 'Default (application-derived: in scope)'}
              />
              <label htmlFor={id} className={`small mb-0 ${cell.in_scope ? '' : 'text-muted'}`}
                     style={{ cursor: 'pointer' }}>
                {fw}
              </label>
              {cell.is_override && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-1 text-muted"
                  style={{ fontSize: '0.7rem', lineHeight: 1 }}
                  disabled={saving}
                  onClick={() => onResetCell(ident, fw, physical_db)}
                  title="Reset to application-derived default"
                >
                  <i className="fas fa-rotate-left"></i>
                </button>
              )}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">
          <i className="fas fa-shield-halved me-2 text-primary"></i>
          {card?.label || technology} — DB Compliance Matrix
        </h4>
        <div className="d-flex gap-2 align-items-center">
          <div className="form-check form-switch me-2 mb-0">
            <input className="form-check-input" type="checkbox" role="switch" id="showInactiveSwitch"
              checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} disabled={loading} />
            <label className="form-check-label small text-muted" htmlFor="showInactiveSwitch">
              Show inactive
            </label>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openCreate} disabled={loading || saving}>
            <i className="fas fa-plus me-1"></i>Add Database
          </button>
          <button className="btn btn-success btn-sm" onClick={handleExportCSV} disabled={loading}>
            <i className="fas fa-file-download me-1"></i>Export {techLabel} CSV
          </button>
          <input type="file" ref={fileInputRef} accept=".csv" onChange={handleImportCSV} style={{ display: 'none' }} />
          <button className="btn btn-warning btn-sm" onClick={() => fileInputRef.current?.click()} disabled={loading || importing}>
            {importing
              ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Importing…</>
              : <><i className="fas fa-file-upload me-1"></i>Import {techLabel} CSV</>}
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchCard} disabled={loading || saving}>
            <i className="fas fa-rotate me-1"></i>Refresh
          </button>
        </div>
      </div>

      <p className="text-muted small">
        Compliance frameworks are derived from each database's application. Tick a cell to
        keep a database in scope for that framework, or untick to exclude it (e.g. in scope
        for HIPAA but not SOX). Columns shown are only the frameworks the applications
        mandate. <span className="fst-italic">Scope applies to campaigns and access reports.</span>
      </p>

      {!unitScopeEnforced && (
        <div className="alert alert-warning py-2 small" role="alert">
          <i className="fas fa-triangle-exclamation me-2"></i>
          <strong>Unit-level scope is recorded but not yet enforced for {card?.label || technology}.</strong>{' '}
          Instance-level (top row) scope filters campaigns and reports normally. Sub-unit toggles
          (PDB / schema / keyspace / database) are saved for the record, but do not yet exclude that
          unit's access data — that requires per-unit collection, tracked separately. MSSQL enforces
          unit-level scope today.
        </div>
      )}

      {notice && (
        <div className={`alert alert-${notice.type} py-2`} role="alert">
          {notice.text}
        </div>
      )}

      {loading && (
        <div className="text-center text-muted py-5">
          <i className="fas fa-spinner fa-spin me-2"></i>Loading…
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-danger">{error}</div>
      )}

      {!loading && !error && frameworks.length === 0 && (
        <div className="alert alert-info">
          No compliance frameworks are mandated by any {card?.label || technology} database's
          application. Set compliance flags on the applications first.
        </div>
      )}

      {!loading && !error && frameworks.length > 0 && (
        <div className="card shadow-sm">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0 align-middle">
                <thead className="table-dark">
                  <tr>
                    <th style={{ minWidth: '260px' }}>Database</th>
                    <th>Application</th>
                    <th style={{ minWidth: '240px' }}>Compliance</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {databases.map((db) => {
                    const units = db.physical_dbs || [];
                    const hasUnits = units.length > 0;
                    // Pluralized unit-kind label for the row's "· N Databases/PDBs/…" hint.
                    const unitLabel = units[0]?.unit_kind_label
                      || ({ ORACLE: 'PDB', MYSQL: 'Schema', CASSANDRA: 'Keyspace' }[(technology || '').toUpperCase()] || 'Database');
                    const unitLabelPlural = unitLabel.endsWith('s') ? unitLabel : `${unitLabel}s`;
                    const isOpen = !!expanded[db.database_identifier];
                    // A mapping with no connection yet is shown muted + badged; it activates
                    // automatically once a connection for it exists (no chicken-and-egg).
                    const inactive = db.is_active === false;  // soft-deleted (only shown when "Show inactive")
                    const pending = !inactive && db.has_connection === false;
                    const noConnBadge = pending && (
                      <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.65rem' }}
                        title="This database has no connection yet. Create a connection for it in Database Connections to activate it.">
                        <i className="fas fa-plug-circle-exclamation me-1"></i>No connection — add one to activate
                      </span>
                    );
                    const inactiveBadge = inactive && (
                      <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem' }}
                        title="This database is deactivated (soft-deleted). It is excluded from reports and new campaigns. Reactivate to bring it back.">
                        <i className="fas fa-ban me-1"></i>Inactive
                      </span>
                    );
                    const muted = pending || inactive;
                    return (
                      <React.Fragment key={db.database_identifier}>
                        {/* instance / mapping row */}
                        <tr className={muted ? 'text-muted fst-italic' : undefined}
                            style={muted ? { opacity: 0.7 } : undefined}>
                          <td>
                            {hasUnits ? (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none"
                                onClick={() => toggleExpand(db.database_identifier)}
                              >
                                <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'} fa-xs me-1`}></i>
                                <span className="fw-semibold">{db.display_name || db.database_identifier}</span>
                                {db.instance && <span className="text-muted"> · {db.instance}</span>}
                                <span className="text-muted"> · {units.length} {unitLabelPlural}</span>
                                {noConnBadge}{inactiveBadge}
                              </button>
                            ) : (
                              <>
                                <span className="fw-semibold">{db.display_name || db.database_identifier}</span>
                                {noConnBadge}{inactiveBadge}
                              </>
                            )}
                          </td>
                          <td className="small">
                            {/* M:N: one chip per linked application; the star marks the
                                PRIMARY app (its owner attests this database). Secondary
                                chips get make-primary and detach actions. */}
                            {(() => {
                              const apps = (db.apps && db.apps.length)
                                ? db.apps
                                : (db.app_id ? [{ app_id: db.app_id, app_name: db.app_name, is_primary: true }] : []);
                              if (apps.length === 0) return <span className="text-muted">—</span>;
                              return apps.filter((a) => a.is_active !== false).map((app) => (
                                <span key={app.app_id}
                                      className="badge bg-light text-dark border me-1 mb-1 align-middle"
                                      style={{ fontWeight: 'normal' }}>
                                  {app.is_primary && (
                                    <i className="fas fa-star text-warning me-1"
                                       title="Primary application — its owner attests this database"></i>
                                  )}
                                  {onNavigateToApplication ? (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-decoration-none align-baseline"
                                      style={{ fontSize: 'inherit' }}
                                      onClick={() => onNavigateToApplication(app.app_name || app.app_id)}
                                      title={`View application details for ${app.app_name || app.app_id} (${app.app_id})`}
                                    >
                                      {app.app_name || app.app_id}
                                    </button>
                                  ) : (
                                    <span className="text-muted">{app.app_name || app.app_id}</span>
                                  )}
                                  {!app.is_primary && !inactive && (
                                    <>
                                      <button type="button"
                                        className="btn btn-link btn-sm p-0 ms-1 text-warning align-baseline"
                                        style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                        title={`Make ${app.app_name || app.app_id} the primary application`}
                                        disabled={saving}
                                        onClick={() => handleSetPrimary(db, app)}>
                                        <i className="far fa-star"></i>
                                      </button>
                                      <button type="button"
                                        className="btn btn-link btn-sm p-0 ms-1 text-danger align-baseline"
                                        style={{ fontSize: '0.7rem', lineHeight: 1 }}
                                        title={`Detach ${app.app_name || app.app_id} from this database`}
                                        disabled={saving}
                                        onClick={() => handleDetachApp(db, app)}>
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </>
                                  )}
                                </span>
                              ));
                            })()}
                            {!inactive && (
                              <button type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none d-block"
                                style={{ fontSize: '0.75rem' }}
                                title="Link another application to this database (multi-app support)"
                                onClick={() => { setLinkTarget(db); setLinkAppId(''); }}>
                                <i className="fas fa-plus fa-xs me-1"></i>Link app
                              </button>
                            )}
                          </td>
                          <td>
                            {inactive
                              ? <span className="text-muted small">Reactivate to edit compliance scope</span>
                              : renderComplianceCell(db.database_identifier, null, db.cells)}
                          </td>
                          <td className="text-center text-nowrap">
                            {inactive ? (
                              <button type="button" className="btn btn-sm btn-outline-success"
                                title="Reactivate database" disabled={saving}
                                onClick={() => handleReactivate(db)}>
                                <i className="fas fa-rotate-left me-1"></i>Reactivate
                              </button>
                            ) : (
                              <>
                                <button type="button" className="btn btn-sm btn-outline-primary me-1"
                                  title="Edit database" disabled={saving}
                                  onClick={() => openEdit(db)}>
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button type="button" className="btn btn-sm btn-outline-warning me-1"
                                  title="Deactivate (archive) database" disabled={saving}
                                  onClick={() => handleDeactivate(db)}>
                                  <i className="fas fa-power-off"></i>
                                </button>
                                <DatabaseDeleteAction
                                  db={{
                                    database_identifier: db.database_identifier,
                                    database_name: db.database_name || db.database_identifier,
                                    display_name: db.display_name,
                                    app_id: db.app_id,
                                    technology_label: technology,
                                    technology,
                                  }}
                                  onDeleted={() => fetchCard()}
                                />
                              </>
                            )}
                          </td>
                        </tr>
                        {/* discovered sub-unit rows (MSSQL DBs / Oracle PDBs / MySQL
                            schemas / Cassandra keyspaces / PostgreSQL databases) */}
                        {hasUnits && isOpen && units.map((pdb) => (
                          <tr key={`${db.database_identifier}::${pdb.name}`} className="table-active">
                            <td>
                              <span className="ms-4 text-muted">
                                <i className="fas fa-database fa-xs me-1"></i>{pdb.name}
                                {pdb.unit_kind_label && (
                                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.65rem' }}>
                                    {pdb.unit_kind_label}
                                  </span>
                                )}
                                {pdb.is_system && (
                                  <span className="text-muted ms-1" style={{ fontSize: '0.65rem' }}
                                    title="System database — rarely in compliance scope">
                                    <i className="fas fa-gear fa-xs me-1"></i>system
                                  </span>
                                )}
                              </span>
                            </td>
                            <td></td>
                            <td>
                              {renderComplianceCell(db.database_identifier, pdb.name, pdb.cells)}
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {databases.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        No active {card?.label || technology} databases. Use <strong>Add Database</strong> to register one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DatabaseMappingModal
        show={showModal}
        onClose={() => { setShowModal(false); setCrudError(null); }}
        mode={modalMode}
        error={crudError}
        appLocked={modalMode === 'edit'}
        formData={formData}
        onFormChange={handleFormChange}
        applications={applications}
        databaseType={card?.label || technology}
        saving={crudSaving}
        onSave={handleSaveMapping}
      />

      {/* M:N link-application picker — small modal listing apps not yet linked to the
          target database. Linking creates a SECONDARY link (primary stays put). */}
      {linkTarget && (
        <div className="modal fade show" style={{
          display: 'block', backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1060, overflow: 'auto'
        }}>
          <div style={{ padding: '80px 20px' }}>
            <div className="modal-content" style={{ maxWidth: '480px', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white py-2">
                <h6 className="modal-title">
                  <i className="fas fa-link me-2"></i>
                  Link application to {linkTarget.display_name || linkTarget.database_identifier}
                </h6>
                <button type="button" className="btn-close btn-close-white"
                        onClick={() => setLinkTarget(null)}></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-2">
                  The database keeps its primary application (starred) — the primary app's
                  owner attests it. Linking adds this database to another application's
                  scope: that app's compliance mandates join the database's scope union.
                </p>
                <select className="form-select" value={linkAppId}
                        onChange={(e) => setLinkAppId(e.target.value)} disabled={linkSaving}>
                  <option value="">Select application…</option>
                  {applications
                    .filter((a) => !(linkTarget.apps || [{ app_id: linkTarget.app_id }])
                      .some((l) => l.app_id === a.app_id && l.is_active !== false))
                    .map((a) => (
                      <option key={a.app_id} value={a.app_id}>{a.app_id} - {a.app_name}</option>
                    ))}
                </select>
              </div>
              <div className="modal-footer py-2">
                <button className="btn btn-secondary btn-sm" onClick={() => setLinkTarget(null)}
                        disabled={linkSaving}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleLinkApp}
                        disabled={linkSaving || !linkAppId}>
                  {linkSaving
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Linking…</>
                    : <><i className="fas fa-link me-1"></i>Link</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* After a database is created, auto-open the tech-specific connection form
          (embedded — it renders its own full-screen modal overlay) prefilled with
          the new database's identifier so the DBA can create the connection that
          activates it without leaving this screen. */}
      {connPopup && (() => {
        const ConnComp = CONN_COMPONENTS[techLower];
        if (!ConnComp) return null;
        return (
          <ConnComp
            embedded={true}
            prefill={{ database_identifier: connPopup.database_identifier }}
            onCreated={() => {
              setConnPopup(null);
              fetchCard();
              setNotice({ type: 'success', text: 'Connection created — database is now active.' });
            }}
            onCancel={() => setConnPopup(null)}
          />
        );
      })()}
    </div>
  );
};

export default DBComplianceMatrix;
